import { Injectable, Injector } from '@angular/core';
import {
    FiBackendService,
    IBackendErrorAgreementContentData,
    IBackendErrorContentData,
    IBackendErrorContentDetails,
    IBackendErrorData,
    IBackendErrorInactiveUnitData,
    IBackendErrorUserEmailInconsistentData,
} from '../fi-backend/fi-backend.service';
import { ICustomerUserContact } from '../fi-shared-interfaces';
import { ILoginData } from '@fi-shared/fi-auth-shared/fi-auth-shared';
import { ActionFailureReasonCode } from '@fi-shared/types/action-failed-reason-code';
import { StatusCodeType } from '@fi-shared/types';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { Subject } from 'rxjs';
import { FiUserEmailInconsistentDialogComponent } from '@fi-shared/fi-auth-shared/fi-user-email-inconsistent-dialog/fi-user-email-inconsistent-dialog.component';
import { ILightWeightObject } from '@fi-shared/models';
import { ChooseBusinessUnitDialogComponent } from '@fi-shared/fi-user-shared/choose-business-unit-dialog/choose-business-unit-dialog.component';

export interface IUserContactUniqueData {
    IsUnique: boolean;
    ContactValue: string;
    OtherUserHasContactAsLogin: boolean;
}

@Injectable()
export abstract class FiAuthenticateSharedService {
    // todo use in FiAuthenticateHelperService
    protected fiBackend: FiBackendService;
    protected fiDialogsService: FiDialogsService;
    protected appTextService: AppTextService;

    constructor(injector: Injector) {
        this.fiBackend = injector.get(FiBackendService);
        this.fiDialogsService = injector.get(FiDialogsService);
        this.appTextService = injector.get(AppTextService);
    }

    requestAutoLoginV2(contact: ICustomerUserContact) {
        return this.fiBackend.fetchDataPost('Core/Authenticate/RequestAutoLoginV2', contact);
    }

    getContactUniqueData(userId: number, contactValues: string[]): Promise<IUserContactUniqueData[]> {
        return this.fiBackend.fetchDataPost<IUserContactUniqueData[]>(
            `Core/Authenticate/GetContactUniqueData/${userId ?? '0'}`,
            contactValues,
        );
    }

    private _userLoginRetry$ = new Subject<ILoginData>();
    get userLoginRetry$() {
        return this._userLoginRetry$.asObservable();
    }
    protected triggerUserLoginRetry(loginData: ILoginData) {
        this._userLoginRetry$.next(loginData);
    }

    processLoginError(response: IBackendErrorData, loginData: ILoginData) {
        const data: IBackendErrorContentDetails = response.error?.Data,
            reason: number = response.error?.Reason;

        if (!this.processLoginCommonErrorData(response)) {
            if (reason === ActionFailureReasonCode.User_LicenseAgreementMustAccept) {
                this.onUserLicenseAgreementMustAccept(data, loginData);
            } else if (reason === ActionFailureReasonCode.User_ConfidentialAgreementMustAccept) {
                this.onUserConfidentialAgreementMustAccept(data, loginData);
            } else if (reason === ActionFailureReasonCode.User_BusinessUnitDeactivated) {
                this.onUserBusinessUnitDeactivated(data, loginData);
            } else if (reason == ActionFailureReasonCode.User_EmailInconsistent) {
                this.onUserEmailInconsistent(data, loginData);
            } else {
                this.fiDialogsService.error({
                    messageText: this.appTextService.get('common.login_error_temporary'),
                });
            }
        }
    }

    processLoginCommonErrorData(response: IBackendErrorData): boolean {
        const data: IBackendErrorContentData = response.error,
            status: number = response.status,
            reason: number = data?.Reason;
        const ignoredReasonCodes = [
            ActionFailureReasonCode.User_BusinessUnitDeactivated,
            ActionFailureReasonCode.User_EmailInconsistent,
        ];

        if (status === StatusCodeType.BadRequest) {
            if (data?.Description?.MessageText && ignoredReasonCodes.indexOf(reason) === -1) {
                this.fiDialogsService.error({
                    messageText: data.Description.MessageText,
                    headerText: data.Description.HeaderText,
                });
                return true;
            }
        }
        return false;
    }

    onUserEmailInconsistent(data: IBackendErrorUserEmailInconsistentData, loginData: ILoginData) {
        this.fiDialogsService
            .custom({
                topTitleText: this.appTextService.get('login.signin_update_title'),
                topTitleIconClass: 'fa-solid fa-seal-exclamation',
                headerText: this.appTextService.get('login.choose_login_email_header'),
                yesButtonText: this.appTextService.get('common.confirm'),
                isNoButtonHidden: true,
                contentComponentRef: FiUserEmailInconsistentDialogComponent,
                contentComponentData: data,
                config: {
                    //'maxWidth': '340px'
                },
            })
            .onYesButtonClick((result) => {
                this.fiBackend
                    .fetchDataPost<void>(
                        `Core/Authenticate/UpdateUserLoginEmail/${data.UserId}?email=${encodeURIComponent(result.selectedEmail)}`,
                    )
                    .then(() => {
                        loginData.userCredentials.Login = result.selectedEmail;
                        this.triggerUserLoginRetry(loginData);
                        // todo other login types
                    });
            });
    }

    abstract logout(): void;
    abstract onUserLicenseAgreementMustAccept(data: IBackendErrorAgreementContentData, loginData: ILoginData);
    abstract onUserConfidentialAgreementMustAccept(data: IBackendErrorAgreementContentData, loginData: ILoginData);

    //#region user inactive business unit
    onUserBusinessUnitDeactivated(data: IBackendErrorInactiveUnitData, loginData: ILoginData) {
        let activeUnits = data.BusinessUnits.filter((unit) => unit.IsActivated === true);
        if (!activeUnits.length) {
            this.fiDialogsService.error({
                headerText: this.appTextService.get('common.login_error_user_business_unit_deactivated_header'),
                messageText: this.appTextService.get('common.login_error_user_business_unit_deactivated'),
            });
        } else {
            this.changeUnit(activeUnits, data.Token, loginData);
        }
    }
    private changeUnit(activeUnits: ILightWeightObject[], token: string, loginData: ILoginData) {
        if (activeUnits.length === 1) {
            this.setNewBusinessUnit(activeUnits[0].Id, token, loginData);
        } else if (activeUnits.length > 1) {
            this.fiDialogsService
                .custom({
                    headerText: this.appTextService.get('user.unit_deactivated_header'),
                    yesButtonText: this.appTextService.get('common.switch_unit'),
                    contentComponentRef: ChooseBusinessUnitDialogComponent,
                    contentComponentData: {
                        businessUnits: activeUnits,
                    },
                    config: {
                        maxWidth: '340px',
                    },
                })
                .onYesButtonClick((newBusinessUnit) => this.setNewBusinessUnit(newBusinessUnit.Id, token, loginData));
        } else {
            this.fiDialogsService.error({
                messageText: this.appTextService.get('common.login_error_user_business_unit_deactivated'),
            });
        }
    }

    private setNewBusinessUnit(newBusinessUnitId: number, token: string, loginData: ILoginData) {
        this.changeUserBusinessUnitLogin(newBusinessUnitId, token).then(() => this.triggerUserLoginRetry(loginData));
    }
    private changeUserBusinessUnitLogin(businessUnitId: number, token: string) {
        return this.fiBackend.fetchDataPost<void>('Core/User/SetupPrefferredBusinessUnit', {
            Token: token,
            BusinessUnitId: businessUnitId,
        });
    }
    //#endregion
}
